import { type FC, useEffect } from 'react';

import { Plus } from 'lucide-react';
import { type UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { useGetAssetsStatusesQuery } from '@features/asset-management/api/asset-management.api';
import { AssetStatus } from '@features/asset-management/asset-management.types';
import { SUPPORTED_FILE_TYPES } from '@features/investigations/api/files.types';
import { TokenLimitAlert } from '@features/investigations/components/modals/add-new-investigation/TokenLimitAlert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@shared/components/ui';
import { GenericButton, GenericDateRangePicker, GenericMultiSelect } from '@shared/components/ui/custom';

import { useAddNewTrace } from './hooks/useAddNewTrace';
import { type NewInvestigationFormValues, TARGET_TYPES } from './investigation.schema';
import { getAssetOptions } from './investigation.utils';
import { TargetRow } from './TargetRow';

export interface NewInvestigationFormProps {
  form: UseFormReturn<NewInvestigationFormValues>;
  onClose: () => void;
  onSuccess: () => void;
  hasReachedLimit: boolean;
  isEstimating: boolean;
  estimationHasError: boolean;
  markAsLaunched: () => void;
  resetAllMutations: () => void;
}

// TODO(file-launch-renaming): rename form component/props/types to neutral file-launch naming (investigation + inquiry).
export const NewInvestigationForm: FC<NewInvestigationFormProps> = ({
  form,
  onClose,
  onSuccess,
  hasReachedLimit,
  isEstimating,
  estimationHasError,
  markAsLaunched,
  resetAllMutations,
}) => {
  const { data: assetsData = [], isLoading: isAssetsLoading } = useGetAssetsStatusesQuery();
  const assetOptions = getAssetOptions(assetsData ?? []);
  const readyAssets = (assetsData ?? []).filter((asset) => asset.status === AssetStatus.READY);
  const hasReadyAssetsInResponse = readyAssets.length > 0;
  const defaultAssetNames = readyAssets.map((a) => a.name);

  const { submitNewTrace, isLoading } = useAddNewTrace();
  // TODO(file-launch-renaming): rename submit hook API away from legacy "Trace" wording.

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'targets',
  });

  const watchedTargets = useWatch({ control: form.control, name: 'targets' });
  const watchedType = useWatch({ control: form.control, name: 'type' });
  const isInvestigationMode = watchedType === 'INVESTIGATION';

  const usedTypes = new Set(watchedTargets?.map((t) => t.type).filter(Boolean));

  useEffect(() => {
    if (isAssetsLoading) return;

    if (!hasReadyAssetsInResponse) {
      form.setError('assets', {
        type: 'manual',
        message: 'At least one READY application instance is required to proceed.',
      });
      return;
    }

    if (form.getValues('assets')?.length === 0) {
      form.setValue('assets', defaultAssetNames, { shouldDirty: false, shouldValidate: false });
    }

    form.clearErrors('assets');
  }, [isAssetsLoading, hasReadyAssetsInResponse, form, defaultAssetNames]);

  const syncUsernameField = (toInquiry: boolean) => {
    const targets = form.getValues('targets') ?? [];
    const usernameTargetIndex = targets.findIndex((target) => target.type === 'user');

    if (toInquiry) {
      const targetUserValue = targets[usernameTargetIndex]?.value ?? '';
      form.setValue('inquiryUsername', targetUserValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    } else if (usernameTargetIndex >= 0) {
      const inquiryValue = form.getValues('inquiryUsername') ?? '';
      form.setValue(`targets.${usernameTargetIndex}.value`, inquiryValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  };

  const handleTypeChange = (nextType: string) => {
    if (nextType !== 'INVESTIGATION' && nextType !== 'INQUIRY') {
      return;
    }

    form.setValue('type', nextType, {
      shouldDirty: true,
      shouldValidate: false,
    });

    if (nextType === 'INQUIRY') {
      form.clearErrors('targets');
      syncUsernameField(true);
      return;
    }

    form.clearErrors('inquiryUsername');
    syncUsernameField(false);
  };

  const handleAddCondition = () => {
    const available = TARGET_TYPES.find((t) => !usedTypes.has(t));
    if (available) {
      append({ type: available, value: '' });
    }
  };

  const handleOnClose = () => {
    onClose();
    resetAllMutations();
  };

  const handleValidSubmit = async (data: NewInvestigationFormValues) => {
    try {
      await submitNewTrace({ ...data, sendEmailNotification: true }, form.setError);
      markAsLaunched();
      handleOnClose();
      onSuccess();
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  const isLaunchDisabled =
    isEstimating ||
    form.formState.isSubmitting ||
    !!form.formState.errors.assets ||
    (isInvestigationMode && (estimationHasError || hasReachedLimit));

  return (
    <Form {...form} data-testid="add-investigation-form-root">
      <form data-testid="add-investigation-form">
        <div
          className="flex-1 overflow-y-auto scroll-container bg-background p-8 max-h-[80vh]"
          data-testid="add-investigation-modal-scroll-area"
        >
          <div className="w-full max-w-[640px] mx-auto space-y-6 gap-1" data-testid="add-investigation-form-fields">
            <FormField
              name="type"
              render={() => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-file-type">
                  <FormLabel className="flex items-center gap-2" data-testid="add-investigation-label-file-type">
                    File Type
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-file-type">
                    <RadioGroup
                      onValueChange={handleTypeChange}
                      value={watchedType ?? 'INVESTIGATION'}
                      className="flex flex-row gap-6"
                      data-testid="add-investigation-type-group"
                    >
                      {SUPPORTED_FILE_TYPES.map((fileType) => (
                        <div
                          key={fileType}
                          className="flex items-center gap-2"
                          data-testid={`add-investigation-type-option-${fileType.toLowerCase()}`}
                        >
                          <RadioGroupItem
                            value={fileType}
                            id={`file-type-${fileType}`}
                            data-testid={`add-investigation-type-radio-${fileType.toLowerCase()}`}
                          />
                          <Label
                            htmlFor={`file-type-${fileType}`}
                            className="capitalize"
                            data-testid={`add-investigation-type-label-${fileType.toLowerCase()}`}
                          >
                            {fileType.toLowerCase()}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-file-type" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="traceName"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-name">
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="add-investigation-label-name"
                    htmlFor="trace-name"
                  >
                    Name
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-name">
                    <Input
                      placeholder="Enter a unique name"
                      {...field}
                      data-testid="add-investigation-input-name"
                      id="trace-name"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-name" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dateRange"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-daterange">
                  <FormLabel className="flex items-center gap-2" data-testid="add-investigation-label-daterange">
                    Time Range
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-daterange">
                    <GenericDateRangePicker
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full"
                      data-testid="add-investigation-picker-daterange"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-daterange" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assets"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-application-instances">
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="add-investigation-label-application-instances"
                  >
                    Application Instances
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-application-instances">
                    <GenericMultiSelect
                      options={assetOptions}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder={
                        isAssetsLoading ? 'Loading application instances...' : 'Select application instances'
                      }
                      searchPlaceholder="Search application instances..."
                      className="w-full"
                      data-testid="add-investigation-multiselect-application-instances"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-application-instances" />
                </FormItem>
              )}
            />

            <div className="space-y-3" data-testid="add-investigation-scope-section">
              <span
                id="add-investigation-scope-label"
                className="text-sm font-medium leading-none"
                data-testid="add-investigation-label-scope"
              >
                Scope
              </span>
              <fieldset
                className="border border-border rounded-lg p-4 space-y-4 pb-10"
                aria-labelledby="add-investigation-scope-label"
                data-testid="add-investigation-scope-list"
              >
                {isInvestigationMode ? (
                  <>
                    {fields.map((field, index) => (
                      <TargetRow
                        key={field.id}
                        form={form}
                        index={index}
                        onRemove={remove}
                        usedTypes={usedTypes}
                        isLast={index === fields.length - 1}
                      />
                    ))}

                    {usedTypes.size < TARGET_TYPES.length && (
                      <div className="pt-2" data-testid="add-investigation-scope-add-btn-wrapper">
                        <GenericButton
                          type="button"
                          variant="ghost"
                          onClick={handleAddCondition}
                          className="text-primary hover:bg-primary/10 pl-0 h-auto font-normal"
                          label="New condition"
                          icon={Plus}
                          iconPosition="left"
                          data-testid="add-investigation-scope-add-btn"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <FormField
                    control={form.control}
                    name="inquiryUsername"
                    render={({ field }) => (
                      <FormItem className="w-full" data-testid="add-investigation-formitem-inquiry-username">
                        <FormLabel
                          className="flex items-center gap-2"
                          data-testid="add-investigation-label-inquiry-username"
                        >
                          Username
                        </FormLabel>
                        <FormControl data-testid="add-investigation-control-inquiry-username">
                          <Input
                            placeholder="Enter Username"
                            data-testid="add-investigation-input-inquiry-username"
                            id="inquiry-username"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage data-testid="add-investigation-message-inquiry-username" />
                      </FormItem>
                    )}
                  />
                )}
              </fieldset>
            </div>

            {isInvestigationMode && <TokenLimitAlert hasReachedLimit={hasReachedLimit} />}

            <div className="flex items-center gap-3" data-testid="add-investigation-actions">
              <GenericButton
                variant="secondary"
                data-testid="add-investigation-cancel-btn"
                onClick={handleOnClose}
                type="button"
                label="Cancel"
              />
              <GenericButton
                label="Save & Launch"
                type="submit"
                isLoading={isLoading}
                onClick={form.handleSubmit(handleValidSubmit)}
                data-testid="add-investigation-save-btn"
                disabled={isLaunchDisabled || (!form.formState.isValid && form.formState.isSubmitted)}
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
